<script lang="ts">
	import { _ } from 'svelte-i18n';
	import { fade, fly } from 'svelte/transition';
	import { onMount } from 'svelte';
	import Header from '$lib/components/sections/Header.svelte';
	import Footer from '$lib/components/sections/Footer.svelte';
	import SeoContent from '$lib/components/sections/SeoContent.svelte';
	import Button from '$lib/components/primitives/Button.svelte';
	import Icon from '$lib/components/primitives/Icon.svelte';

	let visible = $state(true); // SSR: start visible so hero + <h1> are in server HTML (Ahrefs: "H1 tag missing"). onMount re-set is a no-op; entrance animations still play on client-side navigation.
	onMount(() => {
		visible = true;
	});

	const problems = [
		{ text: $_('features.notes.problems.problem1') },
		{ text: $_('features.notes.problems.problem2') },
		{ text: $_('features.notes.problems.problem3') },
		{ text: $_('features.notes.problems.problem4') }
	];

	const featureList = [
		{
			icon: 'folder-open',
			title: $_('features.notes.features.feature1.title'),
			description: $_('features.notes.features.feature1.description')
		},
		{
			icon: 'type',
			title: $_('features.notes.features.feature2.title'),
			description: $_('features.notes.features.feature2.description')
		},
		{
			icon: 'users',
			title: $_('features.notes.features.feature3.title'),
			description: $_('features.notes.features.feature3.description')
		}
	];

	const useCases = [
		{
			icon: 'user',
			title: $_('features.notes.useCases.case1.title'),
			description: $_('features.notes.useCases.case1.description')
		},
		{
			icon: 'briefcase',
			title: $_('features.notes.useCases.case2.title'),
			description: $_('features.notes.useCases.case2.description')
		},
		{
			icon: 'users',
			title: $_('features.notes.useCases.case3.title'),
			description: $_('features.notes.useCases.case3.description')
		}
	];
</script>

<svelte:head>
	<title>{$_('features.notes.meta.title')}</title>
	<meta name="description" content={$_('features.notes.meta.description')} />
	<meta property="og:title" content="{$_('features.notes.meta.title')}" />
	<meta property="og:description" content={$_('features.notes.meta.description')} />
</svelte:head>

<Header />

<div class="feature-page">
	<section class="hero">
		<div class="container">
			{#if visible}
				<div class="hero__content" in:fade={{ duration: 600, delay: 100 }}>
					<div class="hero__eyebrow" in:fly={{ y: 20, duration: 500, delay: 200 }}>
						<Icon name="file-text" size={20} />
						<span>{$_('features.notes.hero.eyebrow')}</span>
					</div>
					<h1 class="hero__title" in:fly={{ y: 30, duration: 600, delay: 300 }}>
						{@html $_('features.notes.hero.title')}
					</h1>
					<p class="hero__subtitle" in:fly={{ y: 30, duration: 600, delay: 400 }}>
						{$_('features.notes.hero.subtitle')}
					</p>
					<div class="hero__actions" in:fly={{ y: 30, duration: 600, delay: 500 }}>
						<Button variant="primary" size="lg" href="https://app.heyweek.com/?signup">
							{$_('features.notes.hero.cta')}
						</Button>
					</div>
				</div>
				<div class="hero__visual" in:fly={{ y: 30, duration: 600, delay: 600 }}>
					<div class="hero__screenshot-frame">
						<div class="hero__screenshot-chrome">
							<span class="chrome-dot"></span>
							<span class="chrome-dot"></span>
							<span class="chrome-dot"></span>
						</div>
						<img
							src="/images/feat-notes.png"
							alt="Notes and documentation in Heyweek"
							class="hero__screenshot-img"
							loading="eager"
						/>
					</div>
				</div>
			{/if}
		</div>
	</section>

	<section class="problems">
		<div class="container">
			<div class="problems__content">
				<div class="problems__left">
					<h2 class="problems__title">{$_('features.notes.problems.title')}</h2>
					<div class="problems__list">
						{#each problems as problem}
							<div class="problem-item">
								<Icon name="x" size={18} />
								<span>{problem.text}</span>
							</div>
						{/each}
					</div>
				</div>
				<div class="problems__right">
					<div class="solution-card">
						<h3 class="solution-card__title">{$_('features.notes.solution.title')}</h3>
						<ul class="solution-card__list">
							<li>{$_('features.notes.solution.point1')}</li>
							<li>{$_('features.notes.solution.point2')}</li>
							<li>{$_('features.notes.solution.point3')}</li>
							<li>{$_('features.notes.solution.point4')}</li>
						</ul>
						<Button variant="secondary" size="md" href="#features">
							{$_('features.notes.solution.cta')}
						</Button>
					</div>
				</div>
			</div>
		</div>
	</section>

	<section class="features" id="features">
		<div class="container">
			<div class="features__grid">
				{#each featureList as feature}
					<div class="feature-block">
						<div class="feature-block__icon">
							<Icon name={feature.icon} size={32} />
						</div>
						<h3 class="feature-block__title">{feature.title}</h3>
						<p class="feature-block__description">{feature.description}</p>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<section class="use-cases">
		<div class="container">
			<div class="use-cases__header">
				<h2 class="use-cases__title">{$_('features.notes.useCases.title')}</h2>
			</div>
			<div class="use-cases__grid">
				{#each useCases as uc}
					<div class="use-case">
						<div class="use-case__icon">
							<Icon name={uc.icon} size={24} />
						</div>
						<h3 class="use-case__title">{uc.title}</h3>
						<p class="use-case__description">{uc.description}</p>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<section class="cta">
		<div class="container">
			<div class="cta__content">
				<h2 class="cta__title">{$_('features.notes.cta.title')}</h2>
				<p class="cta__subtitle">{$_('features.notes.cta.subtitle')}</p>
				<div class="cta__actions">
					<Button variant="primary" size="lg" href="https://app.heyweek.com/?signup">
						{$_('features.notes.cta.primaryButton')}
					</Button>
					<Button variant="ghost" size="lg" href="/pricing">
						{$_('features.notes.cta.secondaryButton')}
					</Button>
				</div>
			</div>
		</div>
	</section>
</div>

<SeoContent slug="notes" />

<Footer />

<style>
	.feature-page {
		min-height: 100vh;
	}

	/* Hero Section */
	.hero {
		padding: var(--space-20) 0;
		overflow: hidden;
	}

	.hero__content {
		max-width: 700px;
		margin: 0 auto;
		text-align: center;
	}

	.hero__eyebrow {
		display: inline-flex;
		align-items: center;
		gap: var(--space-2);
		padding: var(--space-1) var(--space-3);
		background: hsl(var(--brand-h) var(--brand-s) var(--brand-l) / 0.1);
		color: var(--text-brand);
		border-radius: var(--radius-full);
		font-size: var(--font-size-sm);
		font-weight: var(--font-weight-medium);
		margin-bottom: var(--space-4);
	}

	.hero__title {
		font-size: var(--font-size-5xl);
		font-weight: var(--font-weight-bold);
		line-height: 1.1;
		color: var(--text-primary);
		margin-bottom: var(--space-6);
		letter-spacing: -0.02em;
	}

	.hero__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		line-height: var(--line-height-relaxed);
		margin-bottom: var(--space-8);
	}

	.hero__actions {
		display: flex;
		gap: var(--space-4);
		justify-content: center;
	}

	/* Hero Visual */
	.hero__visual {
		margin-top: var(--space-12);
		max-width: min(1000px, 100%);
		margin-inline: auto;
	}

	.hero__screenshot-frame {
		border-radius: 12px;
		overflow: hidden;
		border: 1px solid var(--border-light);
		box-shadow:
			0 24px 80px hsla(0, 0%, 0%, 0.15),
			0 8px 24px hsla(0, 0%, 0%, 0.08);
	}

	.hero__screenshot-chrome {
		display: flex;
		align-items: center;
		gap: 6px;
		padding: 9px 14px;
		background: var(--bg-secondary);
		border-bottom: 1px solid var(--border-light);
	}

	.chrome-dot {
		width: 11px;
		height: 11px;
		border-radius: 50%;
		flex-shrink: 0;
	}

	.chrome-dot:nth-child(1) {
		background: hsl(0 75% 58%);
	}
	.chrome-dot:nth-child(2) {
		background: hsl(38 82% 56%);
	}
	.chrome-dot:nth-child(3) {
		background: hsl(130 52% 46%);
	}

	.hero__screenshot-img {
		display: block;
		width: 100%;
		height: auto;
		transition: filter 0.3s ease;
	}

	:global([data-theme='dark']) .hero__screenshot-img {
		filter: invert(1) hue-rotate(180deg) brightness(0.9) saturate(0.85);
	}

	/* Problems Section */
	.problems {
		padding: var(--space-20) 0;
	}

	.problems__content {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--space-12);
		align-items: center;
		max-width: 1100px;
		margin: 0 auto;
	}

	.problems__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-8);
		letter-spacing: -0.02em;
	}

	.problems__list {
		display: flex;
		flex-direction: column;
		gap: var(--space-4);
	}

	.problem-item {
		display: flex;
		align-items: center;
		gap: var(--space-3);
		font-size: var(--font-size-base);
		color: var(--text-secondary);
	}

	.problem-item :global(svg) {
		color: hsl(0 72% 55%);
		flex-shrink: 0;
	}

	.solution-card {
		padding: var(--space-8);
		background: linear-gradient(
			135deg,
			hsl(var(--brand-h) var(--brand-s) var(--brand-l) / 0.05),
			transparent
		);
		border: 1px solid hsl(var(--brand-h) var(--brand-s) var(--brand-l) / 0.2);
		border-radius: var(--radius-lg);
	}

	.solution-card__title {
		font-size: var(--font-size-2xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-6);
		letter-spacing: -0.02em;
	}

	.solution-card__list {
		list-style: none;
		padding: 0;
		margin: 0 0 var(--space-6) 0;
	}

	.solution-card__list li {
		padding: var(--space-3) 0;
		padding-left: var(--space-8);
		position: relative;
		color: var(--text-primary);
		line-height: 1.6;
	}

	.solution-card__list li::before {
		content: '✓';
		position: absolute;
		left: 0;
		color: var(--text-brand);
		font-weight: var(--font-weight-bold);
	}

	/* Features Section */
	.features {
		padding: var(--space-20) 0;
		background: var(--bg-secondary);
	}

	.features__grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: var(--space-8);
		max-width: 1100px;
		margin: 0 auto;
	}

	.feature-block {
		text-align: center;
	}

	.feature-block__icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 80px;
		height: 80px;
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-lg);
		margin: 0 auto var(--space-4);
		color: var(--text-brand);
	}

	.feature-block__title {
		font-size: var(--font-size-xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-3);
	}

	.feature-block__description {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		line-height: 1.6;
	}

	/* Use Cases */
	.use-cases {
		padding: var(--space-20) 0;
	}

	.use-cases__header {
		text-align: center;
		margin-bottom: var(--space-12);
	}

	.use-cases__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		letter-spacing: -0.02em;
	}

	.use-cases__grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: var(--space-6);
		max-width: 1100px;
		margin: 0 auto;
	}

	.use-case {
		padding: var(--space-6);
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-lg);
	}

	.use-case__icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 48px;
		height: 48px;
		background: hsl(var(--brand-h) var(--brand-s) var(--brand-l) / 0.1);
		color: var(--text-brand);
		border-radius: var(--radius-md);
		margin-bottom: var(--space-4);
	}

	.use-case__title {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-3);
	}

	.use-case__description {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		line-height: 1.6;
		font-style: italic;
	}

	/* CTA Section */
	.cta {
		padding: var(--space-20) 0;
		text-align: center;
		background: linear-gradient(180deg, transparent 0%, var(--bg-secondary) 100%);
	}

	.cta__content {
		max-width: 700px;
		margin: 0 auto;
	}

	.cta__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
		letter-spacing: -0.02em;
	}

	.cta__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		margin-bottom: var(--space-8);
		line-height: 1.6;
	}

	.cta__actions {
		display: flex;
		gap: var(--space-4);
		justify-content: center;
		flex-wrap: wrap;
	}

	/* Responsive */
	@media (max-width: 768px) {
		.problems__content {
			grid-template-columns: 1fr;
		}

		.features__grid,
		.use-cases__grid {
			grid-template-columns: 1fr;
		}
	}
</style>
